import React, { useState, useRef } from "react";
import { makeStyles, Select as MuiSelect, MenuItem, InputBase, Paper } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SearchablePaperProps extends React.ComponentProps<typeof Paper> {
  searchText: string;
  onSearchChange: (value: string) => void;
  searchClasses: { searchContainer: string; searchInput: string; searchIcon: string };
}

/** MUI clona TODOS los hijos que recibe <Select> (no sólo los MenuItem) y les inyecta su propio
 * onClick, que selecciona el valor y cierra el desplegable. Si el buscador viviera como hijo de
 * Select, clickearlo dispararía ese cierre pese al stopPropagation (ese sólo frena la propagación
 * hacia afuera, no el resto del handler de MUI que sigue corriendo después). Por eso se renderiza
 * acá afuera, inyectado vía MenuProps.PaperProps.component, fuera del alcance de ese clonado.
 * Se define a nivel de módulo (no adentro de Select) para que la referencia de función sea estable
 * entre renders: si cambiara en cada tipeo, MUI la vería como un componente distinto y remontaría
 * el Paper, perdiendo el foco del input en cada letra. */
const SearchablePaper = React.forwardRef<HTMLDivElement, SearchablePaperProps>(
  ({ searchText, onSearchChange, searchClasses, children, ...paperProps }, ref) => (
    <Paper ref={ref} {...paperProps}>
      <div
        className={searchClasses.searchContainer}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SearchIcon className={searchClasses.searchIcon} />
        <InputBase
          placeholder="Buscar..."
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          className={searchClasses.searchInput}
        />
      </div>
      {children}
    </Paper>
  )
);

interface SelectProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  error?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  isSearchable?: boolean;
  focusFirstOptionOnOpen?: boolean;
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#364153",
    marginBottom: "4px",
  },
  requiredAsterisk: {
    color: "#EF4444",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    "&:focus-within": {
      borderColor: "#2B7FFF",
      boxShadow: "0 0 0 3px rgba(43, 127, 255, 0.1)",
    },
  },
  inputWrapperError: {
    borderColor: "#EF4444",
    "&:focus-within": {
      borderColor: "#EF4444",
      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
    },
  },
  inputWrapperDisabled: {
    backgroundColor: "#F8FAFC",
    cursor: "not-allowed",
  },
  select: {
    flex: 1,
    fontSize: "16px",
    color: "#334155",
    width: "100%",
    "& .MuiSelect-select": {
      padding: 7,
      lineHeight: "1.1875",
      "&:focus": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiSelect-icon": {
      color: "#64748B",
    },
  },
  placeholder: {
    color: "#94A3B8",
  },
  helperText: {
    fontSize: "14px",
    marginTop: "4px",
  },
  errorText: {
    color: "#EF4444",
  },
  descriptionText: {
    color: "#64748B",
  },
  searchContainer: {
    padding: "8px 12px",
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 1,
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchInput: {
    fontSize: "14px",
    color: "#334155",
    width: "100%",
    "& input": {
      padding: "4px 0px",
    },
  },
  searchIcon: {
    color: "#94A3B8",
    fontSize: "20px",
  },
  noOptionsText: {
    padding: "12px 16px",
    color: "#64748B",
    fontSize: "14px",
    justifyContent: "center",
  },
}));

const Select = ({
  value,
  onChange,
  options,
  label,
  placeholder,
  error,
  description,
  disabled = false,
  className,
  isSearchable = false,
  focusFirstOptionOnOpen = false,
}: SelectProps) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const firstOptionRef = useRef<HTMLLIElement>(null);

  const handleClose = () => {
    setSearchText("");
  };

  const renderLabel = (labelText: string) => {
    const requiredMatch = labelText.match(/^(.*?)(\s*\*)$/);
    if (!requiredMatch) {
      return labelText;
    }

    return (
      <>
        {requiredMatch[1]}
        <span className={classes.requiredAsterisk}>{requiredMatch[2]}</span>
      </>
    );
  };

  const inputWrapperClasses = [
    classes.inputWrapper,
    error ? classes.inputWrapperError : "",
    disabled ? classes.inputWrapperDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showHelper = !error && description;

  const filteredOptions = isSearchable
    ? options.filter((opt) => opt.label.toLowerCase().includes(searchText.toLowerCase()))
    : options;

  return (
    <div className={[classes.container, className].filter(Boolean).join(" ")}>
      {label && <label className={classes.label}>{renderLabel(label)}</label>}

      <div className={inputWrapperClasses}>
        <MuiSelect
          value={value}
          onChange={(e) => onChange(e.target.value as string | number)}
          disabled={disabled}
          className={classes.select}
          input={<InputBase />}
          displayEmpty
          disableUnderline
          onClose={isSearchable ? handleClose : undefined}
          renderValue={(selected) => {
            if (selected === "" || selected === undefined || selected === null) {
              return <span className={classes.placeholder}>{placeholder}</span>;
            }
            const selectedOption = options.find((opt) => opt.value === selected);
            return selectedOption ? selectedOption.label : (selected as string);
          }}
          MenuProps={{
            autoFocus: false,
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PaperProps: isSearchable
              ? ({
                  component: SearchablePaper,
                  searchText,
                  onSearchChange: setSearchText,
                  searchClasses: {
                    searchContainer: classes.searchContainer,
                    searchInput: classes.searchInput,
                    searchIcon: classes.searchIcon,
                  },
                  style: {
                    maxHeight: 300,
                  },
                } as unknown as React.ComponentProps<typeof Paper>)
              : {
                  style: {
                    maxHeight: 300,
                  },
                },
            TransitionProps: isSearchable || focusFirstOptionOnOpen
              ? {
                  onEntered: () => firstOptionRef.current?.focus(),
                }
              : undefined,
          }}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              <span className={classes.placeholder}>{placeholder}</span>
            </MenuItem>
          )}
          {filteredOptions.length === 0 ? (
            <MenuItem disabled className={classes.noOptionsText}>
              No se encontraron resultados
            </MenuItem>
          ) : (
            filteredOptions.map((opt, index) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                ref={index === 0 ? firstOptionRef : undefined}
              >
                {opt.label}
              </MenuItem>
            ))
          )}
        </MuiSelect>
      </div>

      {error && <span className={`${classes.helperText} ${classes.errorText}`}>{error}</span>}

      {showHelper && (
        <span className={`${classes.helperText} ${classes.descriptionText}`}>{description}</span>
      )}
    </div>
  );
};

export default Select;
